import { useEffect, useState, type FormEvent } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Button, Field, Select, useToast } from '@w7/ui'
import { cmApi, type CmSport } from '@/api/cmApi'
import { authHeaders } from '@/auth/session'
import { CmPanel, CmStatus, SPORTS } from './CmPanel'
import { CatalogWorkflow } from './catalogGuide'

function flagKey(sport: CmSport): string {
  if (sport === 'soccer') return 'isAutoFetchSoccer'
  if (sport === 'tennis') return 'isAutoFetchTennis'
  return 'isAutoFetchCricket'
}

function hrsKey(sport: CmSport): string {
  if (sport === 'soccer') return 'isAutoFetchSoccerHrs'
  if (sport === 'tennis') return 'isAutoFetchTennisHrs'
  return 'isAutoFetchCricketHrs'
}

const HORIZON_OPTIONS = [
  { value: '6', label: '6 hours' },
  { value: '12', label: '12 hours' },
  { value: '24', label: '24 hours (1 day)' },
  { value: '48', label: '48 hours (2 days)' },
  { value: '72', label: '72 hours (3 days)' },
]

export function AutomationPage() {
  const toast = useToast()
  const qc = useQueryClient()
  const [sport, setSport] = useState<CmSport>('soccer')
  const [enabled, setEnabled] = useState(false)
  const [hours, setHours] = useState('24')
  const [status, setStatus] = useState('')

  const configQ = useQuery({
    queryKey: ['cm', 'automation', sport],
    queryFn: async () => {
      const res = await cmApi.automationConfig(authHeaders(), { sport, getOnly: true })
      return res.result as { config?: Record<string, unknown> } | Record<string, unknown>
    },
  })

  useEffect(() => {
    const raw = configQ.data
    if (!raw || typeof raw !== 'object') return
    const cfg =
      'config' in raw && raw.config && typeof raw.config === 'object'
        ? (raw.config as Record<string, unknown>)
        : (raw as Record<string, unknown>)
    const on = Boolean(cfg[flagKey(sport)])
    const hrs = cfg[hrsKey(sport)]
    setEnabled(on)
    setHours(hrs == null ? '24' : String(hrs))
    setStatus(
      on
        ? `Auto-fetch on · horizon ${hrs == null ? '24' : String(hrs)}h`
        : 'Auto-fetch off',
    )
  }, [configQ.data, sport])

  const save = useMutation({
    mutationFn: () =>
      cmApi.automationConfig(authHeaders(), {
        sport,
        [flagKey(sport)]: enabled,
        [hrsKey(sport)]: hours.trim() || null,
      }),
    onSuccess: () => {
      toast.success('Automation saved')
      setStatus(
        enabled
          ? `Auto-fetch on · horizon ${hours.trim() || '24'}h`
          : 'Auto-fetch off',
      )
      void qc.invalidateQueries({ queryKey: ['cm', 'automation', sport] })
    },
    onError: (err: Error) => toast.error(err.message),
  })

  const onSubmit = (e: FormEvent) => {
    e.preventDefault()
    save.mutate()
  }

  return (
    <CmPanel
      wide
      title="Automation"
      subtitle="Turn auto-fetch on/off per sport and set how far ahead markets are enabled"
    >
      <div className="cm-catalog-card">
        <CatalogWorkflow
          steps={[
            {
              title: 'Auto-fetch',
              body: 'When on, cron workers pull new events/markets for this sport on schedule.',
            },
            {
              title: 'Enable window',
              body: 'After each fetch, markets are turned on only if kick-off is within the hours you set below.',
            },
            {
              title: 'Example',
              body: '24 hours = enable every match starting in the next day. Use 48 or 72 for longer horizons.',
            },
          ]}
        />

        <p className="cm-horizon-callout">
          <strong>{hours || '24'} hours</strong> means: after auto-fetch runs, turn on markets for
          matches whose start time is within the next <strong>{hours || '24'} hours</strong> from
          now. This is not a repeat interval — it is the enable window per run.
        </p>

        <form className="cm-form" onSubmit={onSubmit}>
          <div className="cm-form-grid">
            <Field label="Sport" required hint="Each sport has its own auto-fetch flag and enable window.">
              {({ id, describedBy }) => (
                <Select
                  id={id}
                  aria-describedby={describedBy}
                  value={sport}
                  onChange={(ev) => setSport(ev.target.value as CmSport)}
                  options={[...SPORTS]}
                />
              )}
            </Field>
            <Field
              label="Enable markets within (hours)"
              hint="How far ahead to enable markets after each auto-fetch. 24 = next 24 hours."
            >
              {({ id, describedBy }) => (
                <Select
                  id={id}
                  aria-describedby={describedBy}
                  value={hours}
                  onChange={(ev) => setHours(ev.target.value)}
                  options={HORIZON_OPTIONS}
                />
              )}
            </Field>
          </div>

          <label className="cm-toggle">
            <input
              type="checkbox"
              checked={enabled}
              onChange={(ev) => setEnabled(ev.target.checked)}
            />
            <span>Auto-fetch enabled for {sport}</span>
          </label>

          <p className="cm-hint">
            Cron workers also need the sport env flag on in deployment. Max events per run is configured
            in Market-API (not editable here).
          </p>

          <div className="cm-actions">
            <Button type="submit" variant="primary" busy={save.isPending}>
              Save automation
            </Button>
            <Button
              type="button"
              variant="secondary"
              disabled={configQ.isFetching}
              onClick={() => void configQ.refetch()}
            >
              Reload
            </Button>
          </div>
        </form>
        <CmStatus value={status} />
      </div>
    </CmPanel>
  )
}
